import type { SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input, ViewContainerRef, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { Ability } from '@core/abilities-types';
import { UserActions, UserSelectors } from '@core/account-data';
import type { User } from '@core/account-types';
import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import { filterBoolean, trackByIndex, trackByProp } from '@core/common-legacy';
import { HelpArticlesDirective } from '@core/help-articles-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { InvitationActions, InvitationSelectors } from '@core/invitations-data';
import type { Invitation } from '@core/invitations-types';
import type { RequisitionAssignment } from '@core/requisitions-types';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  HeadingComponent,
  IconComponent,
  RtTooltip,
} from '@core/user-interface-legacy';
import type { RoleWithAdmins } from '@core/visibility-legacy';
import { InvitationItemComponent, TeamMembersItemComponent } from '@recruitee/memberships-ui';
import { RequisitionAssignmentsActions } from '@recruitee/requisitions-data';

import { TeamMembersFacade } from '../../../facades/single-requisition/team-members.facade';
import { RequisitionsDialogsService } from '../../../services/requisitions-dialogs.service';
import { RequisitionsTeamMembersService } from '../../../services/requisitions-team-members.service';

type AssignmentWithAdmin = RequisitionAssignment & { admin: User };

@Component({
  selector: 'rt-requisitions-team-container',
  templateUrl: './requisitions-team-container.component.html',
  styleUrls: ['./requisitions-team-container.component.less'],
  imports: [
    BoxComponent,
    ButtonComponent,
    HeadingComponent,
    HelpArticlesDirective,
    IconComponent,
    InvitationItemComponent,
    TeamMembersItemComponent,
    TranslatePipe,
    BadgeComponent,
    RtTooltip,
  ],
  providers: [provideAnalyticsViewContext(AnalyticsViewContext.Requisition)],
})
export class RequisitionsTeamContainerComponent {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly requisitionsTeamMembersService = inject(RequisitionsTeamMembersService);
  private readonly teamMembersFacade = inject(TeamMembersFacade);
  private readonly requisitionsDialogsService = inject(RequisitionsDialogsService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly i18n = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly scopeKey = 'invitations:requisition';
  private readonly bulkResendScopeKey = 'invitations:requisition:bulk-resend';
  private readonly bulkDeleteScopeKey = 'invitations:requisition:bulk-delete';

  @Input() public requisitionId: number;
  @Input() public assignments: RequisitionAssignment[] = [];

  public texts = {
    approver: this.i18n.translate('app.common.approver'),
    recruiter: this.i18n.translate('app.common.recruiter'),
    hiringManager: this.i18n.translate('app.common.hiring_manager'),
    cantRemoveTooltip: this.i18n.translate('app.requisitions.team_members.cant_remove_tooltip'),
    removeMemberTooltip: this.i18n.translate('app.requisitions.team_members.remove_member_tooltip'),
  };
  public adminInRemove: number | null = null;
  public invitations: Invitation[] = [];
  public assignmentsWithAdmin: AssignmentWithAdmin[] = [];
  public rolesWithAdmins: RoleWithAdmins[] = [];
  public canEditMembers: boolean;

  public trackById = trackByProp<{ id: string | number }>({ prop: 'id' });
  public trackByIndex = trackByIndex;

  private allowedAdmins: User[] = [];

  private ngOnDestroy$ = new Subject<void>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.assignments) {
      this.setAssignedAdmins();
    }
    this.canEditMembers = this.teamMembersFacade.canEditTeamMembers;
  }

  public ngOnInit(): void {
    this.store
      .select(UserSelectors.entities())
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(admins => {
        this.rolesWithAdmins = this.groupAdminsByRole(admins);
        this.allowedAdmins = admins.filter(
          admin =>
            admin.roleAbilities.includes(Ability.ViewRequisitions) ||
            this.assignments.some(assignment => assignment.adminId === admin.id),
        );

        this.setAssignedAdmins();
        this.cdRef.detectChanges();
      });

    this.store
      .select(
        InvitationSelectors.entitiesWithPagination({
          scope: this.getScopeKey(this.requisitionId),
        }),
      )
      .pipe(
        filter(({ rows, meta }) => !!rows && !!meta),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(data => {
        this.invitations = data.rows;
        this.cdRef.detectChanges();
      });

    this.fetchInvitations();
    this.subscribeToCompanyUpdated();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public isAdminDisabled(assignment: AssignmentWithAdmin): boolean {
    const { isApprover, isRecruiter, isHiringManager } = assignment;
    return isApprover || isRecruiter || isHiringManager;
  }

  public getBadgesTextList(assignment: AssignmentWithAdmin): string[] {
    const { isApprover, isRecruiter, isHiringManager } = assignment;
    const badgesTexts = [];
    if (isApprover) badgesTexts.push(this.texts.approver);
    if (isRecruiter) badgesTexts.push(this.texts.recruiter);
    if (isHiringManager) badgesTexts.push(this.texts.hiringManager);
    return badgesTexts;
  }

  public openTeamMemberDialog(): void {
    this.requisitionsTeamMembersService.editTeamMembers(
      this.assignments,
      this.requisitionId,
      this.viewContainerRef,
    );
  }

  public removeAssignment(removedAdmin: User): void {
    this.requisitionsDialogsService
      .confirmRemoveTeamMember$()
      .pipe(
        filterBoolean,
        tap(() => {
          const assignmentToRemove = this.assignments.find(
            assignment => assignment.adminId === removedAdmin.id,
          )!;
          this.store.dispatch(
            RequisitionAssignmentsActions.removeOnePessimistic({
              payload: {
                data: { assignmentId: assignmentToRemove.id, requisitionId: this.requisitionId },
              },
            }),
          );

          this.adminInRemove = removedAdmin.id;
          this.cdRef.detectChanges();
        }),
        switchMap(() =>
          this.actions$.pipe(ofType(RequisitionAssignmentsActions.removeOnePessimisticSuccess)),
        ),
      )
      .subscribe(() => {
        this.adminInRemove = null;
        this.cdRef.detectChanges();
      });
  }

  public resendInvitation(invitation: Invitation): void {
    this.store.dispatch(
      InvitationActions.bulkResendInvitations({
        payload: { ids: [invitation.id], scope: this.bulkResendScopeKey },
      }),
    );
  }

  public deleteInvitation(invitation: Invitation): void {
    this.requisitionsDialogsService
      .confirmDeleteInvitation$()
      .pipe(filterBoolean)
      .subscribe(() => {
        this.store.dispatch(
          InvitationActions.bulkDeleteInvitations({
            payload: { ids: [invitation.id], scope: this.bulkDeleteScopeKey },
          }),
        );
      });
  }

  private groupAdminsByRole(admins: User[]): RoleWithAdmins[] {
    const groupedAdmins = admins
      .filter(admin => admin.roleAbilities.includes(Ability.ViewAllRequisitions))
      .reduce<Record<string, User[]>>(
        (groups, admin) => ({
          ...groups,
          ...{ [admin.roleId]: [...(groups[admin.roleId] || []), admin] },
        }),
        {},
      );

    return Object.entries(groupedAdmins).map(([roleId, admins]: [string, User[]]) => ({
      id: parseInt(roleId, 10),
      name: admins[0].roleName,
      count: admins.length,
      admins,
    }));
  }

  private getScopeKey(id: number): string {
    return `${this.scopeKey}:${id}`;
  }

  private setAssignedAdmins(): void {
    this.assignmentsWithAdmin = this.assignments.map(assignment => {
      const admin = this.allowedAdmins.find(admin => admin.id === assignment.adminId)!;
      return {
        ...assignment,
        admin,
      };
    });
    this.cdRef.detectChanges();
  }

  private fetchInvitations(): void {
    this.store.dispatch(
      InvitationActions.fetchPaginatedCollection({
        payload: this.getInvitationPayload(),
      }),
    );
  }

  private getInvitationPayload() {
    return {
      params: { page: 1, limit: 1000, requisitionId: this.requisitionId },
      scope: this.getScopeKey(this.requisitionId),
    };
  }

  private subscribeToCompanyUpdated() {
    this.actions$
      .pipe(
        ofType(InvitationActions.invitationsUpdated),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => {
        this.fetchInvitations();
        this.store.dispatch(UserActions.fetchCollection());
      });
  }
}
